import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import { Lock, Sparkles, X } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import { Modal, Platform, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';

import { api } from '~/api';
import { useGoogleIdToken } from '~/lib/googleAuth';
import { loadGoogleIdentity } from '~/lib/googleIdentity';
import { useMember } from '~/stores/useMember';
import { colors, control, fontFamily, price, radii, space, spacing, type } from '~/ui/tokens';
import { GoogleMark } from './GoogleMark';

function MemberButton() {
    const isMember = useMember(state => state.isMember);
    const openPrompt = useMember(state => state.openPrompt);
    const signOut = useMember(state => state.signOut);

    if (isMember) {
        return (
            <View style={styles.badge}>
                <Sparkles size={13} color={colors.brand} />
                <Text style={styles.badgeText}>SuiteScanner+</Text>
                <Pressable
                    accessibilityRole="button"
                    onPress={() => {
                        api.auth.logout().catch(() => undefined);
                        signOut();
                    }}
                >
                    <Text style={styles.signOut}>Sign out</Text>
                </Pressable>
            </View>
        );
    }

    return (
        <Pressable accessibilityRole="button" onPress={openPrompt} style={styles.signIn}>
            <GoogleMark size={17} />
            <Text style={styles.signInText}>Sign in</Text>
        </Pressable>
    );
}

// The gated price on discovery cards: members see the SuiteScanner+ rate with
// the public rate struck through; everyone else sees the public rate and an
// unlock prompt.
function MemberPrice({ guestPrice, memberPrice, align = 'end' }: { guestPrice: number; memberPrice: number; align?: 'start' | 'end' }) {
    const isMember = useMember(state => state.isMember);
    const openPrompt = useMember(state => state.openPrompt);
    const box = [styles.price, align === 'start' ? styles.priceStart : styles.priceEnd];

    if (isMember) {
        return (
            <View style={box}>
                <Text style={styles.guestStruck}>US ${guestPrice}</Text>
                <Text style={styles.memberPrice}>US ${memberPrice}</Text>
                <Text style={styles.memberTag}>/night · SuiteScanner+</Text>
            </View>
        );
    }

    return (
        <View style={box}>
            <Text style={styles.publicPrice}>US ${guestPrice}</Text>
            <Text style={styles.publicTag}>/night · public price</Text>
            {/* No accessibilityRole="button" here: this Pressable can sit inside an outer
                Pressable (e.g. a hotel card), and RN Web renders role="button" as a real
                <button>, which is invalid HTML nested inside another <button>. */}
            <Pressable
                onPress={e => {
                    e.stopPropagation();
                    openPrompt();
                }}
                style={styles.unlock}
            >
                <Lock size={11} color={colors.brand} />
                <Text style={styles.unlockText}>Unlock SuiteScanner+ price</Text>
            </Pressable>
        </View>
    );
}

// Rendered once (root layout). Real Google Sign-In: prompts Google, exchanges
// the resulting id_token with the server for a session token.
function SignInModal() {
    const { width, height } = useWindowDimensions();
    const promptOpen = useMember(state => state.promptOpen);
    const closePrompt = useMember(state => state.closePrompt);
    const signInWithSession = useMember(state => state.signInWithSession);
    const { request, idToken, promptAsync } = useGoogleIdToken();
    const [pending, setPending] = useState(false);
    const [error, setError] = useState(false);
    const router = useRouter();
    // Google's own in-page button (web). Until it has loaded and rendered, and
    // whenever it can't (script blocked, origin not authorised), our own
    // "Continue with Google" button below stays as the fallback.
    const [gsiToken, setGsiToken] = useState<string | null>(null);
    const [gsiReady, setGsiReady] = useState(false);
    const gsiSlotRef = useRef<View>(null);
    const sheetWidth = Math.min(440, width - space.card * 2);
    const ctaWidth = Math.max(200, Math.min(400, sheetWidth - space.cardLarge * 2));
    const [ctaHover, setCtaHover] = useState(false);
    const sheetHeight = Math.min(height - 48, Math.max(300, sheetWidth * 0.9));

    function openPolicy(href: '/terms' | '/privacy') {
        closePrompt();
        router.push(href);
    }

    useEffect(() => {
        if (!promptOpen || Platform.OS !== 'web') return;
        const clientId = process.env.EXPO_PUBLIC_GOOGLE_CLIENT_ID;
        if (!clientId) return;

        let cancelled = false;
        loadGoogleIdentity()
            .then(googleId => {
                if (cancelled) return;
                googleId.initialize({
                    client_id: clientId,
                    callback: response => { if (response.credential) setGsiToken(response.credential); },
                    use_fedcm_for_button: true,
                    itp_support: true,
                });
                // The slot only exists once the modal content has mounted.
                requestAnimationFrame(() => {
                    const slot = gsiSlotRef.current as unknown as HTMLElement | null;
                    if (cancelled || !slot) return;
                    slot.innerHTML = '';
                    googleId.renderButton(slot, {
                        type: 'standard',
                        theme: 'outline',
                        size: 'large',
                        text: 'continue_with',
                        shape: 'rectangular',
                        width: ctaWidth,
                        logo_alignment: 'center',
                    });
                    setGsiReady(true);
                });
            })
            .catch(() => { if (!cancelled) setGsiReady(false); });

        return () => { cancelled = true; };
    }, [promptOpen, ctaWidth]);

    // Either path (library response, or the popup's localStorage hand-off) can
    // deliver the same token; exchange each token once.
    const exchangedToken = useRef<string | null>(null);

    const credential = idToken ?? gsiToken;

    useEffect(() => {
        if (!credential || exchangedToken.current === credential) {
            return;
        }
        exchangedToken.current = credential;

        setPending(true);
        setError(false);

        api.auth
            .google({ idToken: credential })
            .then(result => {
                if (result.data) {
                    signInWithSession(result.data.token, result.data.user);
                } else {
                    setError(true);
                }
            })
            .catch(() => setError(true))
            .finally(() => setPending(false));
    }, [credential, signInWithSession]);

    return (
        <Modal visible={promptOpen} transparent animationType="fade" onRequestClose={closePrompt}>
            <Pressable style={styles.backdrop} onPress={closePrompt}>
                <Pressable style={[styles.sheet, { minHeight: sheetHeight }]} onPress={() => undefined}>
                    <Pressable accessibilityRole="button" accessibilityLabel="Close sign-in" onPress={closePrompt} style={styles.close}>
                        <X size={18} color={colors.inkFaint} />
                    </Pressable>
                    <Image
                        source={require('../../assets/images/favicon-512.png')}
                        contentFit="contain"
                        accessibilityLabel="SuiteScanner"
                        style={styles.sheetLogo}
                    />
                    <Text style={styles.sheetTitle}>Sign in or join for free</Text>
                    <Text style={styles.sheetBody}>SuiteScanner+ gives you access to hotel rates received directly from suppliers, without additional markups.</Text>
                    <View style={[styles.ctaGroup, { width: Math.max(ctaWidth, sheetWidth - space.compact) }]}>
                        <Text style={styles.sheetDisclaimer}>
                            By continuing, you agree to our{' '}
                            <Text accessibilityRole="link" onPress={() => openPolicy('/terms')} style={styles.sheetDisclaimerLink}>Terms of Service</Text>
                            {' '}and{' '}
                            <Text accessibilityRole="link" onPress={() => openPolicy('/privacy')} style={styles.sheetDisclaimerLink}>Privacy Policy</Text>.
                        </Text>
                        <View style={{ alignSelf: 'center', width: ctaWidth }} onPointerEnter={() => setCtaHover(true)} onPointerLeave={() => setCtaHover(false)}>
                            {/* Our own button is what people see, so it matches the rest of
                                the site. When Google's in-page button is available it sits on
                                top, invisible, and receives the click. */}
                            <Pressable
                                accessibilityRole="button"
                                onPress={() => promptAsync()}
                                disabled={!request || pending}
                                style={[styles.googleBtn, ctaHover && styles.googleBtnHover, (!request || pending) && styles.googleBtnDisabled]}
                            >
                                <GoogleMark size={20} />
                                <Text style={styles.googleBtnText}>{pending ? 'Signing in…' : 'Continue with Google'}</Text>
                            </Pressable>
                            {Platform.OS === 'web' ? <View ref={gsiSlotRef} style={[styles.gsiOverlay, { width: ctaWidth }, !gsiReady && styles.gsiOverlayOff]} /> : null}
                        </View>
                    </View>
                    {error ? <Text style={styles.errorNote}>Couldn’t sign in — try again.</Text> : null}
                </Pressable>
            </Pressable>
        </Modal>
    );
}


const styles = StyleSheet.create({
    signIn: { flexDirection: 'row', alignItems: 'center', gap: space.related, borderWidth: 1, borderColor: colors.line, borderRadius: radii.full, backgroundColor: colors.card, paddingVertical: spacing(0.75), paddingHorizontal: spacing(1.5) },
    signInText: { fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize, color: colors.ink },
    badge: { flexDirection: 'row', alignItems: 'center', gap: space.related, backgroundColor: colors.skyTint, borderRadius: radii.full, paddingVertical: spacing(0.75), paddingHorizontal: spacing(1.5) },
    badgeText: { fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize, color: colors.brand },
    signOut: { fontFamily: fontFamily.medium, ...control.utilityCta, color: colors.inkFaint, marginLeft: space.micro },
    price: { gap: space.micro },
    priceStart: { alignItems: 'flex-start' },
    priceEnd: { alignItems: 'flex-end' },
    guestStruck: { fontFamily: fontFamily.regular, fontSize: type.bodyRegular.fontSize, color: colors.inkFaint, textDecorationLine: 'line-through' },
    memberPrice: { fontFamily: fontFamily.bold, ...price.compact, color: colors.ink },
    memberTag: { fontFamily: fontFamily.medium, fontSize: type.metadata.fontSize, color: colors.brand },
    publicPrice: { fontFamily: fontFamily.bold, ...price.compact, color: colors.ink },
    publicTag: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkFaint },
    unlock: { flexDirection: 'row', alignItems: 'center', gap: space.micro, marginTop: space.related, backgroundColor: colors.skyTint, borderRadius: radii.full, paddingVertical: spacing(0.625), paddingHorizontal: spacing(1.25) },
    unlockText: { fontFamily: fontFamily.semibold, ...control.badgeCompact, color: colors.brand },
    backdrop: { flex: 1, backgroundColor: 'rgba(15,23,42,0.55)', alignItems: 'center', justifyContent: 'center', padding: space.card },
    sheet: { width: '100%', maxWidth: 440, backgroundColor: colors.card, borderRadius: radii.lg, padding: space.cardLarge, gap: space.internal, alignItems: 'center', justifyContent: 'center' },
    close: { position: 'absolute', top: spacing(1.5), right: spacing(1.5), padding: spacing(0.5) },
    sheetTitle: { fontFamily: fontFamily.bold, ...type.sectionTitle, color: colors.ink, textAlign: 'center' },
    sheetBody: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft, textAlign: 'center' },
    ctaGroup: { alignSelf: 'center', gap: space.related, marginTop: space.related },
    gsiOverlay: { position: 'absolute', left: 0, top: 0, opacity: 0.01, overflow: 'hidden', zIndex: 2, ...({ transform: 'scaleY(1.3)', transformOrigin: 'top left' } as object) },
    gsiOverlayOff: { width: 1, height: 1, opacity: 0, zIndex: -1, pointerEvents: 'none' as const },
    sheetLogo: { width: 56, height: 56 },
    sheetDisclaimer: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkFaint, textAlign: 'center', lineHeight: 17 },
    sheetDisclaimerLink: { fontFamily: fontFamily.semibold, color: colors.brand, textDecorationLine: 'underline' },
    googleBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: space.compact, alignSelf: 'stretch', minHeight: 48, borderWidth: 1, borderColor: colors.line, borderRadius: radii.md, paddingVertical: spacing(1.5), paddingHorizontal: spacing(3), backgroundColor: colors.card },
    googleBtnHover: { borderColor: colors.brand },
    googleBtnDisabled: { opacity: 0.6 },
    googleBtnText: { fontFamily: fontFamily.semibold, ...control.primaryCta, color: colors.ink },
    errorNote: { fontFamily: fontFamily.medium, fontSize: type.metadata.fontSize, color: colors.brand, textAlign: 'center' },
});

export { MemberButton, MemberPrice, SignInModal };
